'use client'

import React from 'react';
import {
    Box,
    Typography,
    Container,
    List,
    ListItem,
    Divider,
    CssBaseline,
    Button,
    Card,
    Avatar,
    CardContent,
    TextField,
    IconButton,
} from '@mui/material';

import useIsMobile from "@/app/hooks/useIsMobile";
import { Grid, styled, } from "@mui/material";
import dynamic from "next/dynamic";
import { useState } from "react";
import { getDictionary, getField } from "@/app/utils/translation/translation";
import { getSvg } from "@/app/utils/svgUtils";
import { useNavbar } from '@/app/contexts/NavbarContext';
import EastIcon from '@mui/icons-material/East';
import { useRouter } from 'next/navigation';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import GridViewIcon from '@mui/icons-material/GridView';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SortIcon from '@mui/icons-material/Sort';
import useTranslations from '@/app/hooks/useTranslation';

interface BlogDetailsProps {
    translations: any;
    language: any;
    supportedLanguages: any;
    selectedLanguage: any;
    handleLanguageChange: any;
}

const Blogs = () => {
    const {
        loading,
        translations,
        supportedLanguages,
        language,
        selectedLanguage,
        handleLanguageChange,
    } = useTranslations();

    const dictionary = getDictionary(translations, 'blog', language);
    const isArabic = language === 'arabic';
    const { companyRef, productsRef, blogRef, contactusRef, scrollToSection } = useNavbar();
    const [showGridView, setShowGridView] = useState(false)


    const isMobile = useIsMobile();


    const monthToNumber = (month: string) => {
        const months = {
            January: 0, February: 1, March: 2, April: 3,
            May: 4, June: 5, July: 6, August: 7,
            September: 8, October: 9, November: 10, December: 11
        };
        return months[month as keyof typeof months] ?? 0;
    };



    const blogPosts = [
        {
            id: 'efficient-ordering',
            metaTitle: 'Boost Restaurant Success with Cloudix’s All-in-One Ordering System',
            metaDescription: getField(dictionary, 'discover-how-cloudix', language),
            urlSlug: 'efficient-restaurant-ordering-system-unveiling-the-secrets',
            title: getField(dictionary, 'uncover-the-secret', language),
            image: `${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/blogs/Picture1.png`,
            slogan: getField(dictionary, 'slogan1', language),
            date: "6",
            month: 'June',
            day: "FRI",
            year: "2025",

        },
        {
            id: 'reduce-error',
            metaTitle: 'Reduce Human Error in Restaurants with Cloudix Solutions',
            metaDescription: getField(dictionary, 'discover-how-human-error', language),
            urlSlug: 'how-human-error-can-impact-your-restaurant-and-how-cloudix-can-help-you-avoid-them',
            title: getField(dictionary, 'how-human-error-can-impact', language),
            image: `${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/blogs/Picture7.jpg`,
            slogan: getField(dictionary, 'slogan2', language),
            date: "19",
            month: 'June',
            day: "THU",
            year: "2025",

        },
        {
            id: 'digital-ordering',
            metaTitle: 'Reduce Human Error in Restaurants with Cloudix Solutions',
            metaDescription: getField(dictionary, 'the-restaurant-indistry', language),
            urlSlug: 'how-human-error-can-impact-your-restaurant-and-how-cloudix-can-help-you-avoid-them',
            title: getField(dictionary, 'the-ultimate-guide', language),
            image: `${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/blogs/Picture14.png`,
            slogan: getField(dictionary, 'slogan3', language),
            date: "25",
            month: 'June',
            day: "WED",
            year: "2025",

        },
        {
            id: 'transform-your-restaurant',
            metaTitle: 'Maximize Restaurant Sales with Cloudix: The Ultimate Ordering System',
            metaDescription: 'Discover how Cloudix’s restaurant ordering system helps boost efficiency, sales, and customer loyalty. With CRM, online ordering, branded apps, and real-time insights, Cloudix provides everything you need to streamline operations and enhance customer satisfaction.',
            urlSlug: 'transform-your-restaurant-with-cutting-edge-ordering-systems-boost-sales-efficiency-today',
            title: getField(dictionary, 'transform-your-restaurant', language),
            image: `${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/blogs/Picture18.png`,
            slogan: 'The right restaurant ordering system ',
            date: "4",
            month: 'July',
            day: "FRI",
            year: "2025",
        },
        {
            id: 'cloudix-qr-ordering-system',
            metaTitle: 'Cloudix QR Ordering System: Revolutionizing Restaurant Efficiency',
            metaDescription: 'Discover how Cloudix, with its AI-powered features and robust customization, is the ideal QR ordering system for restaurants in the UAE.',
            urlSlug: 'cloudix-qr-ordering-system-revolutionizing-restaurant-efficiency',
            title: 'Cloudix QR Ordering System: Revolutionizing Restaurant Efficiency',
            image: `${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/blogs/Picture22.png`,
            slogan: 'Cloudix QR Ordering: Boost Sales, Cut Costs, Serve Smarter',
            date: "15",
            month: 'July',
            day: "TUE",
            year: "2025",
        }
    ];


    const sortedBlogPosts = [...blogPosts].sort((a, b) => {
        const dateA = new Date(parseInt(a.year), monthToNumber(a.month), parseInt(a.date));
        const dateB = new Date(parseInt(b.year), monthToNumber(b.month), parseInt(b.date));
        return dateB.getTime() - dateA.getTime();
    });

    const router = useRouter();
    const langCode = selectedLanguage === 'arabic' ? 'ar' : selectedLanguage === 'french' ? 'fr' : 'en';
    const handleViewDetails = (id: string) => {
        router.push(langCode === 'en' ? `/blogs/${id}` : `/${langCode}/blogs/${id}`);
    };
    const BlogList = ({ blogPosts, onViewDetails }: { blogPosts: any, onViewDetails: any }) => {

        return (
            <Container
                sx={{
                    py: 4,
                    borderRadius: 3,
                    my: 4,
                }}
            >
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "3rem", flexDirection: "column" }}>
                    <Typography variant={"h2"} component={"h2"} fontFamily={"Century Gothic"} fontWeight={"bold"} fontSize={"30px"} sx={{ color: "var(--text-primary)" }}> {getField(dictionary, 'our-blog', language)} </Typography>
                    <div style={{ marginRight: "5rem" }}>{getSvg('arrowUpPurpleIcon', { darkPurple: 'var(--accent-primary)' }, { width: '60px', height: '60px' }, {})}</div>
                </div>
                <div style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "1rem",
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "var(--section-bg)",
                        borderRadius: "30px",
                        width: isMobile ? "95%" : "40%",
                        height: "50px",
                        boxShadow: "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
                        padding: "0 10px", paddingRight: "30px"
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', marginBottom: "1rem", marginLeft: "20px", width: "67%" }}>
                            <SearchIcon sx={{ color: 'var(--text-primary)', mr: 1, my: 0.5 }} />
                            <TextField
                                id="input-with-sx"
                                label="Search"
                                variant="standard"
                                sx={{ 
                                    width: "100%", 
                                    fontFamily: "Century Gothic",
                                    '& .MuiInputBase-input': {
                                        color: 'var(--text-primary)',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'var(--text-primary)',
                                    },
                                    '& .MuiInput-underline:before': {
                                        borderBottomColor: 'var(--border-color)',
                                    },
                                    '& .MuiInput-underline:hover:before': {
                                        borderBottomColor: 'var(--border-color-hover)',
                                    },
                                    '& .MuiInput-underline:after': {
                                        borderBottomColor: 'var(--accent-primary)',
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: 'var(--accent-primary)',
                                    },
                                }}
                            />
                        </Box>
                        {/* <IconButton><FilterAltIcon sx={{ color: "#4A4848" }} /></IconButton>
                        <IconButton><SortIcon sx={{ color: "#4A4848" }} /></IconButton> */}
                        {!isMobile && (<>
                            <div style={{ display: "flex", marginLeft: isMobile ? "10px" : "auto" }}>
                                <IconButton onClick={() => setShowGridView(false)} aria-label="List View">
                                    <FormatListBulletedIcon sx={{ color: showGridView ? "var(--text-primary)" : "var(--accent-secondary)" }} />
                                </IconButton>
                                <IconButton onClick={() => setShowGridView(true)} aria-label="Grid View">
                                    <GridViewIcon sx={{ color: showGridView ? "var(--accent-secondary)" : "var(--text-primary)" }} />
                                </IconButton>
                            </div>
                        </>)}
                    </div>
                </div>
                {/* Blogs */}
                {isMobile ? (<>
                    <Container>
                        {blogPosts.map((blog: any, index: any) => (
                            <Grid container spacing={4} justifyContent="center" key={index}>
                                <div
                                    style={{
                                        marginTop: "4rem",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        direction: isArabic ? "rtl" : "ltr",
                                        textAlign: isArabic ? "right" : "left",
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        <div>
                                            <img src={blog.image} width="100%" height="100%" alt="Blog" />

                                            <div>
                                                <p style={{ color: "var(--text-secondary)", fontFamily: "Century Gothic" }}>
                                                    {/* Sat , 12 October 2024 */}
                                                    {blog.day} ,{blog.date} {blog.month} {blog.year}
                                                </p>
                                            </div>

                                            <div>
                                                <div style={{ position: "relative", zIndex: 0 }}>
                                                    <Typography
                                                        paragraph
                                                        fontWeight="bold"
                                                        fontSize="17px"
                                                        sx={{ color: "var(--text-primary)" }}
                                                        fontFamily="Century Gothic"
                                                        textTransform="uppercase"
                                                    >
                                                        {blog.title}
                                                    </Typography>
                                                </div>
                                                <div style={{ position: "relative", zIndex: -1 }}>
                                                    <img
                                                        src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/Group 18806.png`}
                                                        loading="lazy"
                                                        alt="Arrow Circle"
                                                        width="170px"
                                                        height="auto"
                                                        style={{
                                                            position: "absolute",
                                                            transform: isArabic ? "translate(-0.1rem, -1rem)" : "translate(0.1rem, -1rem)",
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    width: "100%",
                                                    fontFamily: "Century Gothic",
                                                    fontSize: "15px",
                                                }}
                                            >
                                                <p style={{ color: "var(--text-primary)" }}>{blog.metaDescription}</p>
                                            </div>

                                            <div onClick={() => onViewDetails(blog.id)} style={{ cursor: "pointer" }}>
                                                <p
                                                    style={{
                                                        fontFamily: "Century Gothic",
                                                        display: "flex",
                                                        flexDirection: isArabic ? "row-reverse" : "row",
                                                        alignItems: "flex-start",
                                                        justifyContent: isArabic ? "flex-end" : "flex-start",
                                                        color: "var(--accent-primary)",
                                                        fontSize: "20px",
                                                        fontWeight: 900,
                                                        gap: "1rem",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    {getField(dictionary, "view-blog-details", language)}
                                                    <EastIcon style={isArabic ? { transform: "rotate(180deg)" } : {}} />
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Grid>
                        ))}
                    </Container>
                </>) :
                    (
                        <>
                            {showGridView ? (
                                <Container dir={isArabic ? "rtl" : "ltr"}>
                                    <Grid
                                        container
                                        spacing={6}
                                        justifyContent="center"
                                        alignItems="stretch"
                                        style={{ marginTop: "2rem" }}
                                    >
                                        {blogPosts.map((blog: any, index: number) => (
                                            <Grid item key={index} xs={12} sm={6} md={4}>
                                                <div style={{ direction: isArabic ? "rtl" : "ltr", textAlign: isArabic ? "right" : "left" }}>
                                                    <img
                                                        src={blog.image}
                                                        width="100%"
                                                        height="auto"
                                                        alt="Blog Cover"
                                                    />

                                                    <p style={{ color: "var(--text-secondary)", fontFamily: "Century Gothic" }}>
                                                        {blog.day} ,{blog.date} {blog.month} {blog.year}
                                                    </p>

                                                    <div style={{ position: "relative", zIndex: 0 }}>
                                                        <Typography
                                                            paragraph
                                                            fontWeight="bold"
                                                            fontSize="20px"
                                                            sx={{ color: "var(--text-primary)" }}
                                                            fontFamily="Century Gothic"
                                                            textTransform="uppercase"
                                                        >
                                                            {blog.title}
                                                        </Typography>
                                                        <img
                                                            src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/Group 18806.png`}
                                                            loading="lazy"
                                                            alt="Arrow Circle"
                                                            width="120px"
                                                            height="auto"
                                                            style={{
                                                                position: "absolute",
                                                                transform: isArabic ? "translate(-0.1rem , -1rem)" : "translate(0.1rem , -1rem)",
                                                                zIndex: -1,
                                                            }}
                                                        />
                                                    </div>

                                                    <div style={{ fontFamily: "Century Gothic", fontSize: "15px" }}>
                                                        <p style={{ color: "var(--text-primary)" }}>{blog.metaDescription}</p>
                                                    </div>

                                                    <div onClick={() => onViewDetails(blog.id)} style={{ cursor: "pointer" }}>
                                                        <p
                                                            style={{
                                                                fontFamily: "Century Gothic",
                                                                display: "flex",
                                                                flexDirection: isArabic ? "row-reverse" : "row",
                                                                alignItems: "center",
                                                                justifyContent: isArabic ? "flex-end" : "flex-start",
                                                                color: "var(--accent-primary)",
                                                                fontSize: "20px",
                                                                fontWeight: 900,
                                                                gap: "1rem",
                                                            }}
                                                        >
                                                            {getField(dictionary, "view-blog-details", language)}
                                                            <EastIcon style={isArabic ? { transform: "rotate(180deg)" } : {}} />
                                                        </p>
                                                    </div>
                                                </div>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Container>

                            ) : (
                                <Container>
                                    {blogPosts.map((blog: any, index: any) => {
                                        const isOdd = index % 2 !== 0;
                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    marginTop: "7rem",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    paddingBottom: "5rem",
                                                    direction: isArabic ? "rtl" : "ltr",
                                                    textAlign: isArabic ? "right" : "left",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        gap: "5rem",
                                                        flexDirection: isArabic ? "row-reverse" : "row",
                                                    }}
                                                >

                                                    <div>
                                                        <div style={{ position: "relative", zIndex: 0 }}>
                                                            <div
                                                                style={{
                                                                    color: "var(--accent-primary)",
                                                                    fontSize: "25px",
                                                                    fontWeight: "bold",
                                                                    fontFamily: "Century Gothic",
                                                                    textTransform: "uppercase",
                                                                }}
                                                            >
                                                                <p style={{ margin: 0, padding: 0 }}>{blog.month}</p>
                                                            </div>
                                                        </div>
                                                        <div style={{ position: "relative", zIndex: -1 }}>
                                                            <img
                                                                src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/arrow-line.png`}
                                                                loading="lazy"
                                                                alt="Arrow Circle"
                                                                width="100px"
                                                                height="auto"
                                                                style={{
                                                                    position: "absolute",
                                                                    transform: isArabic ? "scaleX(-1)" : "none",
                                                                }}
                                                            />
                                                        </div>

                                                        <div
                                                            style={{
                                                                textAlign: "center",
                                                                color: "var(--accent-secondary)",
                                                                fontSize: "4rem",
                                                                fontWeight: "bold",
                                                                fontFamily: "Century Gothic",
                                                            }}
                                                        >
                                                            <p style={{ margin: 0, padding: 0 }}>{blog.date}</p>
                                                        </div>
                                                    </div>


                                                    {isOdd ? (
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                                gap: "2rem",
                                                                flexDirection: isArabic ? "row-reverse" : "row",
                                                            }}
                                                        >

                                                            <div>
                                                                <div style={{ position: "relative", zIndex: 0 }}>
                                                                    <Typography
                                                                        paragraph
                                                                        fontWeight="bold"
                                                                        fontSize="20px"
                                                                        sx={{ color: "var(--text-primary)" }}
                                                                        fontFamily="Century Gothic"
                                                                        textTransform="uppercase"
                                                                    >
                                                                        {blog.title}
                                                                    </Typography>
                                                                </div>
                                                                <p style={{ color: "#B2B2B2", fontFamily: "Century Gothic" }}>
                                                                    {blog.day} ,{blog.date} {blog.month} {blog.year}
                                                                </p>
                                                                <div style={{ position: "relative", zIndex: -1 }}>
                                                                    <img
                                                                        src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/Group 18806.png`}
                                                                        loading="lazy"
                                                                        alt="Arrow Circle"
                                                                        width="100px"
                                                                        height="auto"
                                                                        style={{
                                                                            position: "absolute",
                                                                            transform: isArabic ? "translate(-0.1rem , -1rem)" : "translate(0.1rem , -1rem)",
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div
                                                                    style={{
                                                                        width: "100%",
                                                                        fontFamily: "Century Gothic",
                                                                        fontSize: "20px",
                                                                    }}
                                                                >
                                                                    <p style={{ color: "var(--text-primary)" }}>{blog.metaDescription}</p>
                                                                </div>

                                                                <div onClick={() => onViewDetails(blog.id)} style={{ cursor: "pointer" }}>
                                                                    <p
                                                                        style={{
                                                                            fontFamily: "Century Gothic",
                                                                            display: "flex",
                                                                            alignItems: "flex-start",
                                                                            justifyContent: isArabic ? "flex-end" : "flex-start",
                                                                            flexDirection: isArabic ? "row-reverse" : "row",
                                                                            color: "var(--accent-primary)",
                                                                            fontSize: "20px",
                                                                            fontWeight: 900,
                                                                            gap: "1rem",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        {getField(dictionary, "view-blog-details", language)}
                                                                        <EastIcon style={isArabic ? { transform: "rotate(180deg)" } : {}} />
                                                                    </p>
                                                                </div>
                                                            </div>


                                                            <div
                                                                style={{
                                                                    position: "relative",
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                {!isMobile && (
                                                                    <div
                                                                        style={{
                                                                            position: "absolute",
                                                                            top: "-40%",
                                                                            [isArabic ? "right" : "left"]: "30%",
                                                                            zIndex: 1,
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/Repeat Grid.svg`}
                                                                            alt="grid"
                                                                            loading="lazy"
                                                                            width="50%"
                                                                            height="50%"
                                                                        />
                                                                    </div>
                                                                )}
                                                                <img src={blog.image} width="100%" height="100%" style={{ zIndex: 2 }} />
                                                            </div>
                                                        </div>


                                                    ) : (
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                                gap: "2rem",
                                                                width: "100%",
                                                                flexDirection: isArabic ? "row-reverse" : "row",
                                                                direction: isArabic ? "rtl" : "ltr",
                                                                textAlign: isArabic ? "right" : "left",
                                                            }}
                                                        >

                                                            <div
                                                                style={{
                                                                    position: "relative",
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                    width: "40%",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: "-40%",
                                                                        [isArabic ? "right" : "left"]: "30%",
                                                                        zIndex: 1,
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/Repeat Grid.svg`}
                                                                        alt="grid"
                                                                        loading="lazy"
                                                                        width="50%"
                                                                        height="50%"
                                                                    />
                                                                </div>
                                                                <img src={blog.image} width="100%" height="100%" style={{ zIndex: 2 }} />
                                                            </div>


                                                            <div style={{ width: "80%" }}>
                                                                <div>
                                                                    <div style={{ position: "relative", zIndex: 0 }}>
                                                                        <Typography
                                                                            paragraph
                                                                            fontWeight="bold"
                                                                            fontSize="20px"
                                                                            sx={{ color: "var(--text-primary)" }}
                                                                            fontFamily="Century Gothic"
                                                                            textTransform="uppercase"
                                                                        >
                                                                            {blog.title}
                                                                        </Typography>
                                                                    </div>
                                                                    <p style={{ color: "var(--text-secondary)", fontFamily: "Century Gothic" }}>
                                                                        {blog.day} ,{blog.date} {blog.month} {blog.year}
                                                                    </p>
                                                                    <div style={{ position: "relative", zIndex: -1 }}>
                                                                        <img
                                                                            src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/Group 18806.png`}
                                                                            loading="lazy"
                                                                            alt="Arrow Circle"
                                                                            width="200px"
                                                                            height="auto"
                                                                            style={{
                                                                                position: "absolute",
                                                                                transform: isArabic ? "translate(-0.1rem , -1rem)" : "translate(0.1rem , -1rem)",
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    style={{
                                                                        width: "100%",
                                                                        fontFamily: "Century Gothic",
                                                                        fontSize: "20px",
                                                                    }}
                                                                >
                                                                    <p style={{ color: "var(--text-primary)" }}>{blog.metaDescription}</p>
                                                                </div>

                                                                <div onClick={() => onViewDetails(blog.id)} style={{ cursor: "pointer" }}>
                                                                    <p
                                                                        style={{
                                                                            fontFamily: "Century Gothic",
                                                                            display: "flex",
                                                                            alignItems: "flex-start",
                                                                            justifyContent: isArabic ? "flex-end" : "flex-start",
                                                                            flexDirection: isArabic ? "row-reverse" : "row",
                                                                            color: "var(--accent-primary)",
                                                                            fontSize: "20px",
                                                                            fontWeight: 900,
                                                                            gap: "1rem",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        {getField(dictionary, "view-blog-details", language)}
                                                                        <EastIcon style={isArabic ? { transform: "rotate(180deg)" } : {}} />
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Container>
                            )
                            }
                        </>
                    )
                }


            </Container >
        );
    };

    return (

        <>
            <Box
                sx={{
                    minHeight: '100vh',
                    backgroundColor: 'var(--bg-primary)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                }}
            >

                <BlogList blogPosts={sortedBlogPosts} onViewDetails={handleViewDetails} />

            </Box>
        </>
    );
};

export default Blogs;
